import { supabase } from '../supabase';
import type { 
  CreditTransactionResponse, 
  DeductCreditsParams,
  RefundCreditsParams,
  TransactionHistoryItem
} from '../../types/credits';
import { monitoring } from '../../utils/monitoring';

// 缓存配置
const CACHE_TTL = 5000; // 5秒缓存
const cache = new Map<string, { credits: number; timestamp: number }>();

// 清理过期缓存
const cleanupCache = () => {
  const now = Date.now();
  for (const [key, value] of cache.entries()) {
    if (now - value.timestamp > CACHE_TTL) {
      cache.delete(key);
    }
  }
};

// 定期清理缓存
setInterval(cleanupCache, CACHE_TTL);

export const creditsApi = {
  // Check if user has enough credits
  async checkCredits(userId: string, requiredCredits: number): Promise<CreditTransactionResponse> {
    try {
      // 检查缓存
      const cached = cache.get(userId);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        const availableCredits = cached.credits;
        return {
          success: availableCredits >= requiredCredits,
          available_credits: availableCredits,
          required_credits: requiredCredits,
          error: availableCredits < requiredCredits ? 'insufficient_credits' : undefined,
          message: availableCredits < requiredCredits ? 'Insufficient credits' : undefined
        };
      }

      const { data, error } = await supabase
        .from('user_credits')
        .select('available_credits')
        .eq('user_id', userId)
        .single();

      if (error) {
        throw error;
      }

      const availableCredits = Number(data?.available_credits) || 0;
      
      // 更新缓存
      cache.set(userId, { credits: availableCredits, timestamp: Date.now() });

      return {
        success: availableCredits >= requiredCredits,
        available_credits: availableCredits,
        required_credits: requiredCredits,
        error: availableCredits < requiredCredits ? 'insufficient_credits' : undefined,
        message: availableCredits < requiredCredits ? 'Insufficient credits' : undefined
      };
    } catch (err) {
      console.error('Error checking credits:', err);
      monitoring.trackError(err as Error, {
        component: 'creditsApi',
        operation: 'checkCredits',
        userId
      });
      return {
        success: false,
        error: 'api_error',
        message: err instanceof Error ? err.message : 'Failed to check credits'
      };
    }
  },

  // Deduct credits after successful operation
  async deductCredits({
    userId,
    taskId,
    amount,
    description,
    metadata = {}
  }: DeductCreditsParams): Promise<CreditTransactionResponse> {
    try {
      const { data, error } = await supabase.rpc('deduct_credits', {
        p_user_id: userId,
        p_task_id: taskId,
        p_amount: amount,
        p_description: description,
        p_metadata: {
          ...metadata,
          client_timestamp: new Date().toISOString()
        }
      });

      if (error) throw error;

      const result = data as CreditTransactionResponse;
      
      // 更新缓存
      if (result.remaining_credits !== undefined) {
        cache.set(userId, { credits: result.remaining_credits, timestamp: Date.now() });
      }

      return result;
    } catch (err) {
      console.error('Error deducting credits:', err);
      monitoring.trackError(err as Error, {
        component: 'creditsApi',
        operation: 'deductCredits',
        userId,
        taskId
      });
      return {
        success: false,
        error: 'api_error',
        message: err instanceof Error ? err.message : 'Failed to deduct credits'
      };
    }
  },

  // Refund credits
  async refundCredits({
    transactionId,
    amount,
    reason
  }: RefundCreditsParams): Promise<CreditTransactionResponse> {
    try {
      const { data, error } = await supabase.rpc('refund_credits', {
        p_transaction_id: transactionId,
        p_amount: amount,
        p_reason: reason
      });

      if (error) throw error;

      const result = data as CreditTransactionResponse;
      
      // 更新缓存
      if (result.available_credits !== undefined && result.user_id) {
        cache.set(result.user_id, { credits: result.available_credits, timestamp: Date.now() });
      }

      return result;
    } catch (err) {
      console.error('Error refunding credits:', err);
      monitoring.trackError(err as Error, {
        component: 'creditsApi',
        operation: 'refundCredits',
        transactionId
      });
      return {
        success: false,
        error: 'api_error',
        message: err instanceof Error ? err.message : 'Failed to refund credits'
      };
    }
  },

  // Get user's current credits
  async getCurrentCredits(userId: string): Promise<CreditTransactionResponse> {
    try {
      // 检查缓存
      const cached = cache.get(userId);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return {
          success: true,
          available_credits: cached.credits
        };
      }

      const { data, error } = await supabase
        .from('user_credits')
        .select('available_credits')
        .eq('user_id', userId)
        .single();

      if (error) {
        throw error;
      }

      const availableCredits = Number(data?.available_credits) || 0;
      
      // 更新缓存
      cache.set(userId, { credits: availableCredits, timestamp: Date.now() });

      return {
        success: true,
        available_credits: availableCredits
      };
    } catch (err) {
      console.error('Error getting current credits:', err);
      monitoring.trackError(err as Error, {
        component: 'creditsApi',
        operation: 'getCurrentCredits',
        userId
      });
      return {
        success: false,
        error: 'api_error',
        message: err instanceof Error ? err.message : 'Failed to get current credits'
      };
    }
  },

  // Get transaction history
  async getTransactionHistory(
    userId: string,
    limit: number = 10,
    offset: number = 0
  ): Promise<{
    success: boolean;
    data?: TransactionHistoryItem[];
    error?: string;
  }> {
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select(`
          id,
          amount,
          type,
          status,
          description,
          created_at
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      return {
        success: true,
        data: (data || []) as TransactionHistoryItem[]
      };
    } catch (err) {
      console.error('Error fetching transaction history:', err);
      monitoring.trackError(err as Error, {
        component: 'creditsApi',
        operation: 'getTransactionHistory',
        userId
      });
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to fetch transaction history'
      };
    }
  },

  // 清除用户积分缓存
  clearUserCache(userId: string) {
    cache.delete(userId);
  }
}; 